<template>
  <div class="h-full flex flex-col">
    <!-- 顶部标题和搜索栏 -->
    <div class="mb-6">
    <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center border border-blue-500/30">
            <i class="ph-bold ph-users text-blue-400 text-xl"></i>
          </div>
          <div>
            <h2 class="text-2xl font-bold text-white">用户管理</h2>
            <p class="text-sm text-slate-400 mt-0.5">管理系统用户账户和权限</p>
          </div>
        </div>
      </div>
      
      <!-- 搜索和筛选栏 -->
      <div class="flex items-center gap-3">
        <div class="flex-1 relative">
          <i class="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
        <input
          :value="searchKeyword"
            @input="(e) => { $emit('update:search-keyword', e.target.value); $emit('load'); }"
          type="text"
            placeholder="搜索用户名或姓名..."
            class="w-full pl-10 pr-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all"
        />
        </div>
        <div class="relative">
          <i class="ph-bold ph-funnel absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"></i>
          <button
            type="button"
            @click="filterRoleDropdownOpen = !filterRoleDropdownOpen"
            class="min-w-[140px] pl-10 pr-8 py-2.5 bg-slate-900/85 border border-slate-700 rounded-lg text-sm text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 transition-all shadow-[0_0_0_1px_rgba(59,130,246,0.25)] flex items-center justify-between"
          >
            <span class="truncate">{{ currentFilterRoleLabel }}</span>
            <i :class="['ph-bold', filterRoleDropdownOpen ? 'ph-caret-up' : 'ph-caret-down', 'text-slate-400 text-xs']"></i>
          </button>
          <div
            v-if="filterRoleDropdownOpen"
            class="absolute right-0 mt-2 w-44 bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl backdrop-blur p-1 z-30"
          >
            <button
              v-for="opt in filterRoleOptions"
              :key="opt.value"
              type="button"
              @click="selectFilterRoleOption(opt.value)"
              class="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white transition flex items-center justify-between"
              :class="searchRole === opt.value ? 'bg-blue-500/10 border border-blue-500/40' : 'border border-transparent'"
            >
              <span>{{ opt.label }}</span>
              <i v-if="searchRole === opt.value" class="ph-bold ph-check text-blue-400 text-xs"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 表格容器 -->
    <div class="flex-1 overflow-hidden flex flex-col bg-slate-900/30 rounded-xl border border-slate-700/50 backdrop-blur-sm">
      <div v-if="loading" class="flex-1 flex items-center justify-center">
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mb-3"></div>
          <p class="text-slate-400">加载中...</p>
        </div>
    </div>
      
      <div v-else class="flex-1 overflow-auto">
        <table class="w-full">
          <thead class="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50">
            <tr>
              <th class="text-left py-3 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider">ID</th>
              <th class="text-left py-3 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider">用户名</th>
              <th class="text-left py-3 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider">姓名</th>
              <th class="text-left py-3 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider">角色</th>
              <th class="text-left py-3 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider">状态</th>
              <th class="text-left py-3 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider">操作</th>
              <th class="text-right py-3 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider">删除</th>
          </tr>
        </thead>
          <tbody class="divide-y divide-slate-800/50">
            <tr
              v-for="user in users"
              :key="user.id"
              class="group hover:bg-slate-800/30 transition-colors duration-150"
            >
              <!-- ID -->
              <td class="py-4 px-6 text-slate-400 text-sm font-mono">{{ user.id }}</td>

              <!-- 用户名 -->
              <td class="py-4 px-6">
                <span class="text-white font-medium">
                  {{ user.username }}
                </span>
              </td>

              <!-- 姓名 -->
              <td class="py-4 px-6">
                <span class="text-slate-300">
                  {{ user.name || '-' }}
                </span>
              </td>

              <!-- 角色 -->
              <td class="py-4 px-6">
                <span class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium leading-none" :class="getRoleClass(user.role)">
                {{ user.roleLabel || user.role }}
              </span>
            </td>

              <!-- 状态 -->
              <td class="py-4 px-6">
                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
                  :class="user.status === 1 ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'"
                >
                {{ user.status === 1 ? '启用' : '禁用' }}
              </span>
            </td>

              <!-- 操作：打开信息卡片 -->
              <td class="py-4 px-6">
              <button
                  @click="openEditModal(user)"
                  class="h-8 px-4 text-xs font-medium rounded-md transition-all bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20 hover:border-blue-500/50"
                >
                  修改
              </button>
            </td>

              <!-- 删除列（占位，真正删除在卡片内） -->
              <td class="py-4 px-6 text-right">
                <span class="text-slate-700">-</span>
              </td>
          </tr>
        </tbody>
      </table>
        
        <!-- 空状态 -->
        <div v-if="users.length === 0" class="flex items-center justify-center py-16">
          <div class="text-center">
            <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800/50 flex items-center justify-center">
              <i class="ph-bold ph-users text-3xl text-slate-600"></i>
            </div>
            <p class="text-slate-400 text-sm">暂无用户数据</p>
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div v-if="!loading && users.length > 0" class="flex-shrink-0 px-6 py-4 border-t border-slate-700/50 flex items-center justify-between">
        <div class="text-sm text-slate-400">
          共 <span class="text-white font-medium">{{ total }}</span> 条记录，第 <span class="text-white font-medium">{{ page }}</span> / <span class="text-white font-medium">{{ totalPages }}</span> 页
        </div>
        <div class="flex items-center gap-2">
          <button
            @click="$emit('page-change', page - 1)"
            :disabled="page <= 1"
            class="px-3 py-1.5 text-sm rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            :class="page <= 1 ? 'bg-slate-800/50 text-slate-600 border-slate-700' : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
          >
            <i class="ph-bold ph-caret-left"></i>
          </button>
          <div class="flex items-center gap-1">
            <template v-for="p in visiblePages" :key="p">
              <button
                v-if="p !== '...'"
                @click="$emit('page-change', p)"
                class="min-w-[32px] px-2 py-1.5 text-sm rounded-lg border transition-all"
                :class="p === page 
                  ? 'bg-blue-500/20 text-blue-400 border-blue-500/50' 
                  : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
              >
                {{ p }}
              </button>
              <span v-else class="px-2 text-slate-500">...</span>
            </template>
          </div>
          <button
            @click="$emit('page-change', page + 1)"
            :disabled="page >= totalPages"
            class="px-3 py-1.5 text-sm rounded-lg border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            :class="page >= totalPages ? 'bg-slate-800/50 text-slate-600 border-slate-700' : 'bg-slate-800/50 text-slate-300 border-slate-600 hover:bg-slate-700/50 hover:border-blue-500/50'"
          >
            <i class="ph-bold ph-caret-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑信息卡片（模态框） -->
    <transition name="fade-zoom">
      <div
        v-if="editingUser"
        class="fixed inset-0 z-50 flex items-center justify-center"
      >
        <!-- 背景遮罩 -->
        <div
          class="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
          @click="closeEditModal"
        ></div>

        <!-- 卡片主体 -->
        <div
          class="relative z-10 w-full max-w-2xl mx-4 bg-slate-900/95 border border-slate-700/80 rounded-2xl shadow-2xl shadow-blue-900/40 overflow-hidden max-h-[90vh] flex flex-col"
        >
          <!-- 卡片头部 -->
          <div class="px-6 py-4 border-b border-slate-800/80 flex items-center justify-between flex-shrink-0">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
                <i class="ph-bold ph-user-gear text-blue-400 text-xl"></i>
              </div>
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-semibold text-white">
                    编辑用户信息
                  </h3>
                  <!-- 账户类型徽章 -->
                  <span
                    class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                    :class="isParentAccount
                      ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      : 'bg-blue-500/10 text-blue-400 border-blue-500/20'"
                  >
                    {{ isParentAccount ? '父账户' : '子账户' }}
                  </span>
                </div>
                <p class="text-xs text-slate-400">
                  ID {{ editingUser?.id }} · {{ editingUser?.username }}
                </p>
              </div>
            </div>
            <button
              class="text-slate-500 hover:text-slate-300 hover:bg-slate-700/60 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              @click="closeEditModal"
            >
              <i class="ph-bold ph-x text-sm"></i>
            </button>
          </div>

          <!-- 卡片内容（可滚动） -->
          <div class="px-6 py-5 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <!-- 用户名 -->
            <div>
              <label class="block text-xs font-medium text-slate-400 mb-1.5">用户名</label>
              <input
                v-model="editForm.username"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50"
              />
            </div>

            <!-- 姓名 -->
            <div>
              <label class="block text-xs font-medium text-slate-400 mb-1.5">姓名</label>
              <input
                v-model="editForm.name"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50"
              />
            </div>

            <!-- 公司名 -->
            <div>
              <label class="block text-xs font-medium text-slate-400 mb-1.5">公司名</label>
              <input
                v-model="editForm.company"
                type="text"
                class="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50"
                placeholder="请输入公司名称"
              />
            </div>

            <!-- 地址选择（上移，选择栏高度减小） -->
            <div>
              <label class="block text-xs font-medium text-slate-400 mb-1.5">地址</label>
              <div class="grid grid-cols-3 gap-3">
                <!-- 省份选择 -->
                <div class="relative">
                  <button
                    type="button"
                    @click="toggleProvinceDropdown"
                    :disabled="loadingProvinces"
                    class="w-full max-w-[200px] px-3 py-1.5 rounded-lg bg-slate-900/90 border border-slate-700 text-sm text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between shadow-sm hover:border-blue-400/60 transition-all"
                  >
                    <span class="truncate">{{ addressForm.province || '请选择省份' }}</span>
                    <i :class="['ph-bold', provinceDropdownOpen ? 'ph-caret-up' : 'ph-caret-down', 'text-slate-400 text-xs']"></i>
                  </button>
                  <transition name="fade">
                    <div
                      v-if="provinceDropdownOpen"
                      class="absolute z-30 mt-2 w-full bg-slate-900/95 border border-blue-500/30 rounded-xl shadow-2xl backdrop-blur p-2 max-h-40 overflow-auto custom-scrollbar"
                    >
                      <div
                        v-for="province in provinces"
                        :key="province.name"
                        @click="selectProvince(province.name)"
                        class="px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white cursor-pointer flex items-center justify-between transition-colors"
                        :class="addressForm.province === province.name ? 'border border-blue-500/40 bg-blue-500/10' : 'border border-transparent'"
                      >
                        <span class="truncate">{{ province.name }}</span>
                        <i v-if="addressForm.province === province.name" class="ph-bold ph-check text-blue-400"></i>
                      </div>
                    </div>
                  </transition>
                </div>

                <!-- 区县选择 -->
                <div class="relative">
                  <button
                    type="button"
                    @click="toggleDistrictDropdown"
                    :disabled="!addressForm.province || loadingDistricts"
                    class="w-full max-w-[200px] px-3 py-1.5 rounded-lg bg-slate-900/90 border border-slate-700 text-sm text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between shadow-sm hover:border-blue-400/60 transition-all"
                  >
                    <span class="truncate">{{ addressForm.district || '请选择市' }}</span>
                    <i :class="['ph-bold', districtDropdownOpen ? 'ph-caret-up' : 'ph-caret-down', 'text-slate-400 text-xs']"></i>
                  </button>
                  <transition name="fade">
                    <div
                      v-if="districtDropdownOpen"
                      class="absolute z-30 mt-2 w-full bg-slate-900/95 border border-blue-500/30 rounded-xl shadow-2xl backdrop-blur p-2 max-h-40 overflow-auto custom-scrollbar"
                    >
                      <div
                        v-for="district in districts"
                        :key="district.id"
                        @click="selectDistrict(district.name)"
                        class="px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white cursor-pointer flex items-center justify-between transition-colors"
                        :class="addressForm.district === district.name ? 'border border-blue-500/40 bg-blue-500/10' : 'border border-transparent'"
                      >
                        <span class="truncate">{{ district.name }}</span>
                        <i v-if="addressForm.district === district.name" class="ph-bold ph-check text-blue-400"></i>
                      </div>
                    </div>
                  </transition>
                </div>

                <!-- 城市选择 -->
                <div class="relative">
                  <button
                    type="button"
                    @click="toggleCityDropdown"
                    :disabled="!addressForm.district || loadingCities"
                    class="w-full max-w-[200px] px-3 py-1.5 rounded-lg bg-slate-900/90 border border-slate-700 text-sm text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between shadow-sm hover:border-blue-400/60 transition-all"
                  >
                    <span class="truncate">{{ addressForm.city || '请选择区/县' }}</span>
                    <i :class="['ph-bold', cityDropdownOpen ? 'ph-caret-up' : 'ph-caret-down', 'text-slate-400 text-xs']"></i>
                  </button>
                  <transition name="fade">
                    <div
                      v-if="cityDropdownOpen"
                      class="absolute z-30 mt-2 w-full bg-slate-900/95 border border-blue-500/30 rounded-xl shadow-2xl backdrop-blur p-2 max-h-40 overflow-auto custom-scrollbar"
                    >
                      <div
                        v-for="city in cities"
                        :key="city.id"
                        @click="selectCity(city)"
                        class="px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white cursor-pointer flex items-center justify-between transition-colors"
                        :class="addressForm.city === city.name ? 'border border-blue-500/40 bg-blue-500/10' : 'border border-transparent'"
                      >
                        <span class="truncate">{{ city.name }}</span>
                        <i v-if="addressForm.city === city.name" class="ph-bold ph-check text-blue-400"></i>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>

            <!-- 角色 & 状态 -->
            <div class="grid grid-cols-2 gap-4">
              <!-- 角色 -->
              <div>
                <label class="block text-xs font-medium text-slate-400 mb-1.5">角色</label>
                <div class="relative">
                  <button
                    type="button"
                    @click="toggleRoleDropdown"
                    :disabled="editingUser?.role === 'op'"
                    class="w-full h-10 px-3 rounded-lg bg-slate-900/90 border border-slate-700 text-sm text-white text-left flex items-center justify-between shadow-[0_0_0_1px_rgba(59,130,246,0.35)] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 hover:border-blue-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span class="truncate">{{ getRoleLabel(editForm.role) || '选择角色' }}</span>
                    <i :class="['ph-bold', roleDropdownOpen ? 'ph-caret-up' : 'ph-caret-down', 'text-blue-300 text-xs']"></i>
                  </button>
                  <transition name="fade">
                    <div
                      v-if="roleDropdownOpen"
                      class="absolute z-30 mt-2 w-full bg-slate-900/95 border border-blue-500/30 rounded-xl shadow-2xl backdrop-blur p-2 max-h-24 overflow-auto custom-scrollbar"
                    >
                      <div
                        v-for="role in availableRoles"
                        :key="role.value"
                        @click="selectRole(role.value)"
                        class="px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white cursor-pointer flex items-center justify-between transition-colors"
                        :class="editForm.role === role.value ? 'border border-blue-500/40 bg-blue-500/10' : 'border border-transparent'"
                      >
                        <span class="truncate">{{ role.label }}</span>
                        <i v-if="editForm.role === role.value" class="ph-bold ph-check text-blue-400"></i>
                      </div>
                    </div>
                  </transition>
                </div>
                <p v-if="editingUser?.role === 'op'" class="mt-1 text-[11px] text-amber-400">
                  运维管理员角色不可更改
                </p>
              </div>

              <!-- 状态 -->
              <div>
                <label class="block text-xs font-medium text-slate-400 mb-1.5">状态</label>
                <div class="flex items-center gap-3">
                  <button
                    type="button"
                    @click="editForm.status = 1"
                    class="flex-1 h-9 text-xs font-medium rounded-lg border transition-all"
                    :class="editForm.status === 1
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/60 shadow-[0_0_0_1px_rgba(16,185,129,0.35)]'
                      : 'bg-slate-900 text-slate-400 border-slate-700 hover:border-emerald-500/50 hover:text-emerald-200'"
                  >
                    启用
                  </button>
                  <button
                    type="button"
                    @click="editForm.status = 0"
                    class="flex-1 h-9 text-xs font-medium rounded-lg border transition-all"
                    :class="editForm.status === 0
                      ? 'bg-red-500/20 text-red-300 border-red-500/60 shadow-[0_0_0_1px_rgba(248,113,113,0.35)]'
                      : 'bg-slate-900 text-slate-400 border-slate-700 hover:border-red-500/50 hover:text-red-200'"
                  >
                    禁用
                  </button>
                </div>
              </div>
            </div>

            <!-- 父子账户关系 -->
            <div class="pt-2 border-t border-slate-800/50">
              <!-- 子账户显示父账户 -->
              <div v-if="!isParentAccount && parentAccount" class="space-y-2">
                <label class="block text-xs font-medium text-slate-400 mb-1.5">父账户</label>
                <div class="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <i class="ph-bold ph-user-circle text-blue-400"></i>
                    <span class="text-sm text-white font-medium">{{ parentAccount.name || parentAccount.username }}</span>
                    <span class="text-xs text-slate-400">@{{ parentAccount.username }}</span>
                  </div>
                  <span class="px-2 py-0.5 rounded text-[10px] font-medium text-slate-400 bg-slate-700/50">
                    {{ getRoleLabel(parentAccount.role) }}
                  </span>
                </div>
              </div>

              <!-- 父账户显示子账户数量（可点击） -->
              <div v-if="isParentAccount" class="space-y-2">
                <label class="block text-xs font-medium text-slate-400 mb-1.5">子账户</label>
                <button
                  type="button"
                  @click="openSubAccountsModal"
                  class="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:border-blue-500/50 transition-colors flex items-center justify-between group"
                >
                  <div class="flex items-center gap-2">
                    <i class="ph-bold ph-users text-blue-400"></i>
                    <span class="text-sm text-white font-medium">子账户数量</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-slate-300 font-mono">{{ subAccountCount }}</span>
                    <i class="ph-bold ph-caret-right text-slate-400 group-hover:text-blue-400 transition-colors"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- 卡片底部操作 -->
          <div class="px-6 py-4 border-t border-slate-800/80 flex items-center justify-between bg-slate-900/80 flex-shrink-0">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-red-300 hover:text-red-200 hover:bg-red-500/10 transition-colors"
              @click="confirmDeleteInModal"
            >
              <i class="ph-bold ph-trash text-sm"></i>
              删除用户
            </button>

            <div class="flex items-center gap-3">
              <button
                type="button"
                class="px-4 py-2 rounded-lg text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-800/80 transition-colors"
                @click="closeEditModal"
              >
                取消
              </button>
              <button
                type="button"
                class="px-4 py-2 rounded-lg text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 shadow-lg shadow-blue-500/30 transition-all"
                @click="submitEdit"
              >
                保存修改
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 子账户列表弹窗 -->
    <transition name="fade-zoom">
      <div
        v-if="showSubAccountsModal"
        class="fixed inset-0 z-[60] flex items-center justify-center"
        @click.self="closeSubAccountsModal"
      >
        <!-- 背景遮罩 -->
        <div
          class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          @click="closeSubAccountsModal"
        ></div>

        <!-- 小弹窗 -->
        <div
          class="relative z-10 w-full max-w-md mx-4 bg-slate-900/95 border border-slate-700/80 rounded-xl shadow-2xl shadow-blue-900/40 overflow-hidden"
        >
          <!-- 弹窗头部 -->
          <div class="px-4 py-3 border-b border-slate-800/80 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="ph-bold ph-users text-blue-400"></i>
              <h3 class="text-sm font-semibold text-white">子账户列表</h3>
              <span class="px-2 py-0.5 rounded text-[10px] font-medium text-slate-400 bg-slate-700/50">
                {{ subAccountCount }} 个
              </span>
            </div>
            <div class="flex items-center gap-2">
              <button
                type="button"
                @click="loadSubAccounts"
                class="text-slate-400 hover:text-blue-400 transition-colors"
                title="刷新"
              >
                <i class="ph-bold ph-arrow-clockwise text-sm"></i>
              </button>
              <button
                type="button"
                class="text-slate-500 hover:text-slate-300 transition-colors"
                @click="closeSubAccountsModal"
              >
                <i class="ph-bold ph-x text-sm"></i>
              </button>
            </div>
          </div>

          <!-- 弹窗内容 -->
          <div class="px-4 py-3 max-h-96 overflow-auto custom-scrollbar">
            <div v-if="loadingSubAccounts" class="flex items-center justify-center py-8">
              <div class="text-center">
                <div class="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mb-2"></div>
                <p class="text-xs text-slate-400">加载中...</p>
              </div>
            </div>
            <div v-else-if="subAccounts.length === 0" class="flex items-center justify-center py-8">
              <div class="text-center">
                <i class="ph-bold ph-users text-3xl text-slate-600 mb-2"></i>
                <p class="text-xs text-slate-500">暂无子账户</p>
              </div>
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="subAccount in subAccounts"
                :key="subAccount.id"
                class="px-3 py-2.5 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <i class="ph-bold ph-user text-blue-400 flex-shrink-0"></i>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-0.5">
                        <span class="text-sm text-white font-medium truncate">{{ subAccount.name || subAccount.username }}</span>
                        <span class="px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0" :class="getRoleClass(subAccount.role)">
                          {{ subAccount.roleLabel || subAccount.role }}
                        </span>
                      </div>
                      <div class="flex items-center gap-2 text-xs text-slate-400">
                        <span class="font-mono">@{{ subAccount.username }}</span>
                        <span class="text-slate-600">·</span>
                        <span>{{ subAccount.email || '-' }}</span>
                      </div>
                    </div>
                  </div>
                  <span
                    class="px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ml-2"
                    :class="subAccount.status === 1 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'"
                  >
                    {{ subAccount.status === 1 ? '启用' : '禁用' }}
                  </span>
                </div>
              </div>
            </div>
      </div>
    </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { opsApi } from '@/api/ops';
import { userApi } from '@/api/user';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  users: { type: Array, default: () => [] },
  searchKeyword: { type: String, default: '' },
  searchRole: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  getRoleClass: { type: Function, required: true },
  page: { type: Number, default: 1 },
  pageSize: { type: Number, default: 10 },
  total: { type: Number, default: 0 }
});

const emit = defineEmits(['update:search-keyword', 'update:search-role', 'load', 'update-user', 'delete-user', 'page-change']);

const totalPages = computed(() => {
  return Math.ceil(props.total / props.pageSize) || 1;
});

const visiblePages = computed(() => {
  const pages = [];
  const current = props.page;
  const total = totalPages.value;
  
  if (total <= 7) {
    // 如果总页数少于等于7，显示所有页码
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // 显示当前页附近的页码
    if (current <= 4) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(total);
    } else if (current >= total - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(total);
    }
  }
  
  return pages;
});

const filterRoleDropdownOpen = ref(false);
const filterRoleOptions = [
  { value: '', label: '全部角色' },
  { value: 'owner', label: '主账号' },
  { value: 'admin', label: '管理员' },
  { value: 'observer', label: '观察员' },
  { value: 'restricted', label: '受限账号' },
  { value: 'op', label: '运维管理员' }
];

const currentFilterRoleLabel = computed(() => {
  const hit = filterRoleOptions.find(o => o.value === props.searchRole);
  return hit ? hit.label : '全部角色';
});

const selectFilterRoleOption = (val) => {
  filterRoleDropdownOpen.value = false;
  emit('update:search-role', val);
  emit('load');
};

const { addToast } = useToast();

// 当前正在编辑的用户
const editingUser = ref(null);

// 编辑表单
const editForm = ref({
  id: null,
  username: '',
  name: '',
  company: '',
  role: '',
  status: 1,
  ctyId: null
});

// 地址相关
const addressForm = ref({
  province: '',
  district: '',
  city: '',
  ctyId: null
});

const provinces = ref([]);
const districts = ref([]);
const cities = ref([]);
const loadingProvinces = ref(false);
const loadingDistricts = ref(false);
const loadingCities = ref(false);
const provinceDropdownOpen = ref(false);
const districtDropdownOpen = ref(false);
const cityDropdownOpen = ref(false);
const roleDropdownOpen = ref(false);

// 父子账户相关
const parentAccount = ref(null);
const subAccounts = ref([]);
const loadingSubAccounts = ref(false);
const showSubAccountsModal = ref(false);

// 计算子账户数量
const subAccountCount = computed(() => {
  return subAccounts.value.length;
});

// 计算属性：是否为父账户
const isParentAccount = computed(() => {
  return !editingUser.value?.prtId;
});

// 可用角色（不含 op）
const availableRoles = [
  { value: 'owner', label: '主账号' },
  { value: 'admin', label: '管理员' },
  { value: 'observer', label: '观察员' },
  { value: 'restricted', label: '受限账号' }
];

// 获取角色标签
const getRoleLabel = (role) => {
  const roleMap = {
    owner: '主账号',
    admin: '管理员',
    observer: '观察员',
    restricted: '受限账号',
    op: '运维管理员'
  };
  return roleMap[role] || role;
};

// 加载省份列表
const loadProvinces = async () => {
  try {
    loadingProvinces.value = true;
    const res = await userApi.getProvinces();
    if (res?.data) {
      provinces.value = res.data;
    }
  } catch (error) {
    console.error('加载省份列表失败:', error);
    addToast('加载省份列表失败', 'error', 2000);
  } finally {
    loadingProvinces.value = false;
  }
};

// 加载区县列表
const loadDistricts = async (province) => {
  if (!province) {
    districts.value = [];
    cities.value = [];
    return;
  }
  try {
    loadingDistricts.value = true;
    const res = await userApi.getDistricts(province);
    if (res?.data) {
      districts.value = res.data;
    }
    cities.value = [];
  } catch (error) {
    console.error('加载区县列表失败:', error);
    addToast('加载区县列表失败', 'error', 2000);
  } finally {
    loadingDistricts.value = false;
  }
};

// 加载城市列表
const loadCities = async (district) => {
  if (!district) {
    cities.value = [];
    return;
  }
  try {
    loadingCities.value = true;
    const res = await userApi.getCities(district);
    if (res?.data) {
      cities.value = res.data;
    }
  } catch (error) {
    console.error('加载城市列表失败:', error);
    addToast('加载城市列表失败', 'error', 2000);
  } finally {
    loadingCities.value = false;
  }
};

// 选择省份
const selectProvince = async (name) => {
  addressForm.value.province = name;
  provinceDropdownOpen.value = false;
  await loadDistricts(name);
  addressForm.value.district = '';
  addressForm.value.city = '';
  addressForm.value.ctyId = null;
};

// 选择区县
const selectDistrict = async (name) => {
  addressForm.value.district = name;
  districtDropdownOpen.value = false;
  await loadCities(name);
  addressForm.value.city = '';
  addressForm.value.ctyId = null;
};

// 选择城市
const selectCity = (city) => {
  addressForm.value.city = city.name;
  addressForm.value.ctyId = city.id;
  editForm.value.ctyId = city.id;
  cityDropdownOpen.value = false;
};

// 角色选择
const toggleRoleDropdown = () => {
  if (editingUser.value?.role === 'op') return;
  roleDropdownOpen.value = !roleDropdownOpen.value;
};

const selectRole = (roleValue) => {
  if (editingUser.value?.role === 'op') return;
  editForm.value.role = roleValue;
  roleDropdownOpen.value = false;
};

// 切换下拉框
const toggleProvinceDropdown = () => {
  provinceDropdownOpen.value = !provinceDropdownOpen.value;
  if (provinceDropdownOpen.value && provinces.value.length === 0) {
    loadProvinces();
  }
};

const toggleDistrictDropdown = () => {
  districtDropdownOpen.value = !districtDropdownOpen.value;
};

const toggleCityDropdown = () => {
  cityDropdownOpen.value = !cityDropdownOpen.value;
};

// 加载父账户信息
const loadParentAccount = async (userId) => {
  try {
    const res = await opsApi.getParentAccount(userId);
    if (res?.code === 200 && res?.data) {
      parentAccount.value = res.data;
    } else {
      parentAccount.value = null;
    }
  } catch (error) {
    console.error('加载父账户信息失败:', error);
    parentAccount.value = null;
  }
};

// 加载子账户列表
const loadSubAccounts = async () => {
  if (!editingUser.value?.id) return;
  try {
    loadingSubAccounts.value = true;
    const res = await opsApi.getSubAccounts(editingUser.value.id);
    if (res?.code === 200 && res?.data) {
      subAccounts.value = res.data;
    } else {
      subAccounts.value = [];
    }
  } catch (error) {
    console.error('加载子账户列表失败:', error);
    subAccounts.value = [];
    // 如果是404错误，可能是该用户没有子账户或者是子账户本身
    if (error?.response?.status !== 404) {
      addToast('加载子账户列表失败', 'error', 2000);
    }
  } finally {
    loadingSubAccounts.value = false;
  }
};

// 打开子账户弹窗
const openSubAccountsModal = async () => {
  showSubAccountsModal.value = true;
  await loadSubAccounts();
};

// 关闭子账户弹窗
const closeSubAccountsModal = () => {
  showSubAccountsModal.value = false;
};

// 加载用户地址信息
const loadUserAddress = async (ctyId) => {
  if (!ctyId) {
    addressForm.value = {
      province: '',
      district: '',
      city: '',
      ctyId: null
    };
    return;
  }
  try {
    const res = await userApi.getAddressInfo(ctyId);
    if (res?.data) {
      addressForm.value.province = res.data.province || '';
      addressForm.value.district = res.data.district || '';
      addressForm.value.city = res.data.city || '';
      addressForm.value.ctyId = res.data.id;
      
      // 加载对应的区县和城市列表
      if (addressForm.value.province) {
        await loadDistricts(addressForm.value.province);
      }
      if (addressForm.value.district) {
        await loadCities(addressForm.value.district);
      }
    }
  } catch (error) {
    console.error('加载用户地址失败:', error);
  }
};

// 打开编辑卡片
const openEditModal = async (user) => {
  if (!user) return;
  editingUser.value = { ...user };
  editForm.value = {
    id: user.id,
    username: user.username,
    name: user.name || '',
    company: user.company || '',
    role: user.role,
    status: user.status,
    ctyId: user.ctyId || null
  };
  
  // 重置地址表单
  addressForm.value = {
    province: '',
    district: '',
    city: '',
    ctyId: null
  };
  
  // 加载地址信息
  if (user.ctyId) {
    await loadUserAddress(user.ctyId);
  }
  
  // 加载省份列表
  if (provinces.value.length === 0) {
    await loadProvinces();
  }
  
  // 加载父子账户关系
  if (user.prtId) {
    // 子账户，加载父账户信息
    await loadParentAccount(user.id);
    subAccounts.value = [];
  } else {
    // 父账户，预加载子账户数量（不显示列表，只在弹窗中显示）
    parentAccount.value = null;
    // 预加载子账户列表以获取数量
    await loadSubAccounts();
  }
};

// 关闭编辑卡片
const closeEditModal = () => {
  editingUser.value = null;
  parentAccount.value = null;
  subAccounts.value = [];
  showSubAccountsModal.value = false;
  provinceDropdownOpen.value = false;
  districtDropdownOpen.value = false;
  cityDropdownOpen.value = false;
};

// 提交修改
const submitEdit = async () => {
  if (!editForm.value.id) return;
  const payload = {
    username: editForm.value.username,
    name: editForm.value.name,
    company: editForm.value.company,
    role: editForm.value.role,
    status: editForm.value.status
  };
  
  // 如果选择了地址，添加ctyId
  if (addressForm.value.ctyId) {
    payload.ctyId = addressForm.value.ctyId;
  }
  
  await emit('update-user', editForm.value.id, payload);
  closeEditModal();
};

// 卡片里删除用户
const confirmDeleteInModal = async () => {
  if (!editingUser.value?.id) return;
  if (confirm('确定要删除该用户吗？')) {
    await emit('delete-user', editingUser.value.id);
    closeEditModal();
  }
};
</script>

<style scoped>
/* 自定义滚动条 */
.overflow-auto::-webkit-scrollbar,
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.overflow-auto::-webkit-scrollbar-track,
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.3);
  border-radius: 4px;
}

.overflow-auto::-webkit-scrollbar-thumb,
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3);
  border-radius: 4px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover,
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.5);
}

/* 表格行hover效果 */
tbody tr {
  transition: all 0.15s ease;
}

/* 模态卡片淡入动画 */
.fade-zoom-enter-active,
.fade-zoom-leave-active {
  transition: opacity 0.2s ease-out;
}

.fade-zoom-enter-from,
.fade-zoom-leave-to {
  opacity: 0;
}

.fade-zoom-enter-active .relative,
.fade-zoom-leave-active .relative {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}

.fade-zoom-enter-from .relative,
.fade-zoom-leave-to .relative {
  transform: scale(0.95);
  opacity: 0;
}

/* 下拉框淡入动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease-out, transform 0.15s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
